import { useLocation } from "react-router-dom";
import { useLanguage } from "@/hooks/useLanguage";
import { getTranslation, getBaseLanguage } from "@/lib/i18n";
import { BRAND_PERSON_NAME, BRAND_NAME, BRAND_HANDLE, BRAND_SITE_URL , BRAND_OG_IMAGE_URL } from "@/lib/brandConfig";

export const StructuredData = () => {
  const location = useLocation();
  const { currentLanguage } = useLanguage();
  const baseUrl = BRAND_SITE_URL;
  const currentUrl = `${baseUrl}${location.pathname}`;
  
  const siteName = getTranslation(currentLanguage, "common.siteName");
  const description = getTranslation(currentLanguage, "common.description");
  
  // Get page type based on pathname
  const getPageType = (pathname: string) => {
    if (pathname === "/") return "ProfilePage";
    if (pathname.includes("fotos") || pathname.includes("imagenes") || pathname.includes("galeria")) return "ImageGallery";
    if (pathname.includes("videos") || pathname.includes("reels") || pathname.includes("video")) return "VideoGallery";
    if (pathname.includes("content-creator") || pathname.includes("creadora-de-contenido") || pathname.includes("influencer")) return "AboutPage";
    return "WebPage";
  };
  
  const pageType = getPageType(location.pathname);
  
  // Enhanced Person/Profile structured data
  const personSchema = {
    "@context": "https://schema.org",
    "@type": "Person",
    "name": BRAND_PERSON_NAME,
    "alternateName": [`@${BRAND_HANDLE}`, BRAND_NAME],
    "url": baseUrl,
    "sameAs": [
      `https://instagram.com/${BRAND_HANDLE}`,
      `https://www.tiktok.com/@${BRAND_HANDLE}`
    ],
    "jobTitle": getBaseLanguage(currentLanguage) === "es" ? "Creadora de Contenido" : "Content Creator",
    "description": description,
    "image": BRAND_OG_IMAGE_URL,
    "knowsAbout": [
      "Content Creation",
      "Lifestyle",
      "Photography",
      "Social Media Marketing",
      "Nutrition",
      "Beauty",
      "Travel"
    ],
    "worksFor": {
      "@type": "Organization",
      "name": "Independent Creator"
    }
  };
  
  // Website structured data
  const websiteSchema = {
    "@context": "https://schema.org",
    "@type": "WebSite",
    "name": siteName,
    "url": baseUrl,
    "description": description,
    "author": {
      "@type": "Person",
      "name": BRAND_PERSON_NAME
    },
    "potentialAction": {
      "@type": "SearchAction",
      "target": {
        "@type": "EntryPoint",
        "urlTemplate": `${baseUrl}/?q={search_term_string}`
      },
      "query-input": "required name=search_term_string"
    }
  };
  
  // Page-specific structured data
  const pageSchema = {
    "@context": "https://schema.org",
    "@type": pageType,
    "name": document.title || siteName,
    "url": currentUrl,
    "description": description,
    "author": {
      "@type": "Person",
      "name": BRAND_PERSON_NAME
    },
    "dateModified": new Date().toISOString(),
    "inLanguage": currentLanguage, // Already includes regional code
    "isPartOf": {
      "@type": "WebSite",
      "name": siteName,
      "url": baseUrl
    }
  };
  
  // BreadcrumbList for current page
  const breadcrumbSchema = {
    "@context": "https://schema.org",
    "@type": "BreadcrumbList",
    "itemListElement": [
      {
        "@type": "ListItem",
        "position": 1,
        "name": getBaseLanguage(currentLanguage) === "es" ? "Inicio" : "Home",
        "item": baseUrl
      },
      ...(location.pathname !== "/" ? [{
        "@type": "ListItem",
        "position": 2,
        "name": document.title || "Page",
        "item": currentUrl
      }] : [])
    ]
  };
  
  return (
    <>
      <script
        type="application/ld+json"
        dangerouslySetInnerHTML={{ __html: JSON.stringify(personSchema) }}
      />
      <script
        type="application/ld+json"
        dangerouslySetInnerHTML={{ __html: JSON.stringify(websiteSchema) }}
      />
      <script
        type="application/ld+json"
        dangerouslySetInnerHTML={{ __html: JSON.stringify(pageSchema) }}
      />
      <script
        type="application/ld+json"
        dangerouslySetInnerHTML={{ __html: JSON.stringify(breadcrumbSchema) }}
      />
    </>
  );
};
